<script setup lang='ts'>
import {computed, onMounted, ref, watch, onUnmounted} from 'vue';
import FlightOptions from "@/components/FlightOptions/FlightOptions.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import {useRoute, useRouter} from "vue-router";
import { useWindowSize } from '@vueuse/core';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ImageGallery from "@/components/ImageGallery/ImageGallery.vue";
import {useI18n} from 'vue-i18n';
import LocationPin from "@/components/Map/LocationPin.vue";
import FAQ from "@/components/FAQ/FAQ.vue";
import fallbackImage from '@/assets/images/no_picture.png';
import CustomButton from "@/components/Button/Button.vue";
import TripAddModal from "@/components/TripCard/TripAddModal.vue";
import {useTripsStore} from "@/stores/tripsStore";
import { useUserStore } from "@/stores/userStore";
import { bookTripOffer } from "@/services/trip.service";
import {VueSpinner} from "vue3-spinners";
import { getAsset } from "@/helpers/assetsUtils";
import {Trip} from "@/models/Trip";
import {useOfferStore} from "@/stores/offerStore";
import YachtAttributes from "@/components/Yacht/YachtAttributes.vue";
import YachtOverview from "@/components/Yacht/YachtOverview.vue";
import YachtAmenities from "@/components/Yacht/YachtAmenities.vue";
import YachtGallery from "@/components/Yacht/YachtGallery.vue";
import YachtDateAndPassengers from "@/components/Yacht/YachtDateAndPassengers.vue";
import YachtSecurePayments from "@/components/Yacht/YachtSecurePayments.vue";
import YachtPricingSummary from "@/components/Yacht/YachtPricingSummary.vue";
import YachtStickyHeader from "@/components/Yacht/YachtStickyHeader.vue";

const {t} = useI18n(); // Import the translation function
const route = useRoute();
const router = useRouter();
const offerStore = useOfferStore();

const trip = ref<Trip | null>(null);

const savedOfferStatus = ref<string | null>(null);
const showAddTripModal = ref(false);
const tripsStore = useTripsStore();

function closeMapPopup() {
  const mapOverlay = document.getElementById("map-overlay");
  if (mapOverlay) mapOverlay.classList.add("hidden");
}

const showFlightOptionsModal = ref(false);
const showImageGalleryModal = ref(false);
const windowCurrSize = ref();
const passengersChanged = ref(false);
const amenitiesChanged = ref(false);
const savingChanges = ref(false);
let originalPassengers = 0;
let originalAmenities = [];


watch(
    () => offerStore.selections.persons,
    (newVal) => {
      passengersChanged.value = newVal !== originalPassengers;
    }
);

watch(
    () => offerStore.selectedAmenities,
    (newVal) => {
      // Compare arrays
      amenitiesChanged.value = !arraysEqual(newVal, originalAmenities);
    },
    { deep: true }
);

// Function to compare arrays
function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((val) => b.includes(val));
}

const tripUuid = computed(() => trip.value.uuid);

function buildOfferPayload() {
  return {
    yachtId: offerStore.yacht.id,
    name: offerStore.yacht.name,
    basePrice:offerStore.priceDetails.basePrice,
    discount: offerStore.availabilities[1]?.discount,
    offerPrice: offerStore.priceDetails.total, 
    dateStart: trip.value?.dateStart,
    dateEnd: trip.value?.dateEnd,
    destination: offerStore.yacht?.location?.id ?? null,
    amenities: offerStore.optionalAmenities,
    selectedAmenities: offerStore.selectedAmenities,
    passengers: offerStore.selections.persons
  };
}

async function updateChanges() {
  savingChanges.value = true;
  const offerPayload = buildOfferPayload();
  await tripsStore.updateTripOffer(tripUuid.value, offerUuid, offerPayload) //uuid from query and payload
  savingChanges.value = false;
  // Once updated, reset state
  if (passengersChanged.value) {
    console.log('Passengers updated to: ', offerStore.selections.persons);
    originalPassengers = offerStore.selections.persons;
    passengersChanged.value = false;
  }

  if (amenitiesChanged.value) {
    console.log('Amenities updated to: ', offerStore.selectedAmenities.map(a => a.id));
    originalAmenities = [...offerStore.selectedAmenities.map(a => a.id)];
    amenitiesChanged.value = false;
  }
}

// Decides whether to show the button
const showUpdateButton = computed(() => passengersChanged.value || amenitiesChanged.value);

function windowSize() {
  windowCurrSize.value = innerWidth;
}

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);
const windowWidth = ref(window.innerWidth);


function updateWindowWidth() {
  windowWidth.value = window.innerWidth;
}

function mainImageCarousel(mainImage, altImages) {
  let result = [{id: 1999999, url: mainImage}, ...altImages];

  return result;
}


onUnmounted(() => {
  offerStore.resetState()
  window.removeEventListener("resize", updateWindowWidth);
});

onMounted(async () => {
  offerStore.resetState()
  const userStore = useUserStore();
  await userStore.restoreSession();

  const tripUuid = String(route.params.tripUuid ?? '');
  const yachtUuid = String(route.params.yachtUuid ?? '');

  try {

    const data = await tripsStore.getTripOfferYacht(tripUuid, yachtUuid);
    trip.value = data?.trip;
    if(trip.value.state === 'planned') {
      console.log("State planned")
      router.push(`/my/trip/${tripUuid}`)
    }
    const offer = data?.offer;
    offerStore.setFromOffer(offer);

    // Capture trip status from saved offer
    savedOfferStatus.value = trip.value?.state ?? null;

    originalPassengers = offerStore.selections.persons;
    originalAmenities = [...offerStore.selectedAmenities.map(a => a.id)];

  } catch (e) {
    console.warn('[SavedOffer] Failed to hydrate from saved offer context', e);
  }

  windowSize();
  window.addEventListener("resize", updateWindowWidth);
  window.addEventListener("resize", windowSize);
});

const offerUuid = String(route.params.yachtUuid || '');

async function handleSavedOfferBooking() {
  isBookingOffer.value = true;
  const tripUuid = String(route.params.tripUuid || '');
  const offerUuid = String(route.params.yachtUuid || '');

  if (!tripUuid || !offerUuid) {
    console.warn('[handleSavedOfferBooking] Missing UUIDs', { tripUuid, offerUuid });
    return;
  }

  try {
    await updateChanges();
    console.log('[SavedOffer] Sending booking request', { tripUuid, offerUuid });
    await bookTripOffer(tripUuid, offerUuid);

    // reload trip to get new state
    await tripsStore.loadTrip(tripUuid);
    // Now state is updated
    router.push({
      name: 'trip-overview',
      params: {
        locale: route.params.locale,
        id: tripUuid
      }
    });

  } catch (error) {
    console.error('[SavedOffer] Booking failed', error);
  } finally {
    isBookingOffer.value = false;
  }
}

const isBookingOffer = ref(false);

const canBookSavedOffer = computed(() => {
  return savedOfferStatus.value === 'offer';
});

const showRequestedSavedOffer = computed(() => {
  return savedOfferStatus.value !== null && savedOfferStatus.value !== 'offer' && savedOfferStatus.value !== 'planned';
});

const selectedAmenitiesIds = computed({
  get() {
    // return only the ids
    return offerStore.selectedAmenities.map(a => a.id);
  },
  set(ids: number[]) {
    // convert ids back to full objects
    offerStore.selectedAmenities = offerStore.optionalAmenities
        .filter(a => ids.includes(Number(a.id)))
        .map(a => ({ ...a, id: Number(a.id) })); // ensure id is number
  }
});
</script>

<template>

  <!-- Initial page loader -->
  <div
      v-if="!offerStore.yacht"
      class="flex justify-center items-center min-h-[600px]"
  >
    <VueSpinner size="40" color="#1B84FF" />
  </div>

  <CustomModal :customIndex="1000" :show="showImageGalleryModal" style="z-index: 100 !important" :mobileClose="true"
               extraClasses="gallery-popup h-[85vh] md:h-[95vh] w-[96vw] overflow-hidden"
               @close="showImageGalleryModal = false">
    <image-gallery
        :images="mainImageCarousel(offerStore.yacht.mainImage.url || offerStore.yacht.mainImage.name || fallbackImage, offerStore.yacht.images)"
        @close="showImageGalleryModal = false"/>
  </CustomModal>

  <!--  On scroll -->
  <YachtStickyHeader
      :yacht="offerStore.yacht"
      :availability="offerStore.availabilities[1]"
      :price-details="offerStore.priceDetails"
      :selected-dates-text="offerStore.selectedAvailabilityDatesText"
      :selections="offerStore.selections"
  />
  
  <div
      class="main-yacht-page-container flex flex-col mt-8"
      v-if="offerStore.yacht"
  >

    <div class="mb-6 container">
      <CustomButton
          type="outline-black"
          :outlined="true"
          :rounded="true"
          :icon="getAsset('icons', 'Arrow_left_16x16.svg')"
          :hoverIcon="getAsset('icons', 'Arrow_left_white_16x16.svg')"
          @click="router.go(-1)"
      >
        {{ t('goback') }}
      </CustomButton>
    </div>
    <!-- Update Passengers Button -->
    <div
        v-if="showUpdateButton"
        class="fixed right-5 flex justify-end mb-4 z-[9999]"
    >
      <CustomButton type="primary"
                    :rounded="true"
                    :outlined="false"
                    class="w-100 px-5"
                    @click="updateChanges"
      >
      <span class="flex items-center gap-2">
        <VueSpinner v-if="savingChanges" :size="16" color="#FFFFFF"/>
        <span>{{ t('saveChanges') }}</span>
      </span>

      </CustomButton>
    </div>
    <!-- Overview Section -->
    <section id="overview">
      <div class="boat-data-header container border rounded-8xl">
        <!--      Header Top-->
        <div class="boat-data-header-top min-h-[350px] pt-11 pb-8 gap-4 flex justify-between bg-gradient-to-r rounded-8xl from-gradients-ghostWhite to-gradients-aliceBlue">
          
          <div class="boat-data-header-left px-6 sm:pl-10 sm:pr-6 mb-2 flex-1 flex flex-col gap-4 w-2/3">
            <!-- Top content -->
            <div class="flex flex-col gap-3">
              <LocationPin v-model="offerStore.yachtLocation" class="location flex gap-1" />
              <div class="boat-name flex align-center gap-x-4">
                <div class="boat-name__text responsive-title-3 font-bold" v-if="offerStore.yacht">
                  {{ offerStore.yacht.model?.name }} · {{ offerStore.yacht.year }} | {{ offerStore.yacht.name }}
                </div>
              </div>
            </div>

            <YachtDateAndPassengers
                v-model:dateFrom="offerStore.selections.dateFrom"
                v-model:dateTo="offerStore.selections.dateTo"
                v-model:passengers="offerStore.selections.persons"
                :passengers-options="offerStore.passengers"
                :disabled-dates="true"
            />
            
            <div class="flex-grow"></div>
            
            <!-- Secure Payments -->
            <YachtSecurePayments/>

          </div>
          <div class="boat-data-header-right sm:py-11 px-6 sm:pr-10 sm:pl-6 flex flex-col gap-y-4 relative w-1/3">

            <YachtPricingSummary
                :availabilities="offerStore.availabilities[1]"
                :price-details="offerStore.priceDetails"
                :yacht="offerStore.yacht"
                :date-days="offerStore.dateDays"
                :loading="offerStore.availabilities[1]?.state === 'loading'"
            >
              <template #button>
                <div>
                  <div class="mt-4" v-if="canBookSavedOffer">
                    <CustomButton type="black"
                                  :rounded="true"
                                  :outlined="false"
                                  class="w-100 my-0"
                                  @click="handleSavedOfferBooking"
                    >
                      <div v-if="isBookingOffer" class="flex justify-center items-center">
                        <VueSpinner size="25" class="p-2" color="white"/>
                      </div>
                      <div v-else>{{ t('book') }}</div>
                    </CustomButton>
                  </div>
                  <!--                  <div v-if="showRequestedSavedOffer" class="mt-4">-->
                  <!--                    <CustomButton type="black"-->
                  <!--                                  :rounded="true"-->
                  <!--                                  :outlined="false"-->
                  <!--                                  class="w-100 my-0"-->
                  <!--                                  :disabled="true"-->
                  <!--                    >-->
                  <!--                      {{ t('bookedfferRequest') }}-->
                  <!--                    </CustomButton>-->
                  <!--                  </div>-->
                </div>
              </template>
            </YachtPricingSummary>
          </div>
        </div>
      </div>

      <YachtOverview
          :yacht="offerStore.yacht"
          :equipment="offerStore.equipment"
          :fallbackImage="fallbackImage"
          @open-gallery="showImageGalleryModal = true"
          class="boat-data-header-bottom"
      />
      
      <hr/>

      <YachtAttributes :yacht="offerStore.yacht"/>
      
    </section>

    <!-- Gallery & Price Includes Section -->
    <YachtGallery
        :images="offerStore.yacht.images"
        :is-mobile="isMobile"
        @openGallery="showImageGalleryModal = true"
    />

    <!-- Addons Section -->
    <YachtAmenities
        :mandatory-amenities="offerStore.mandatoryAmenities"
        :optional-amenities="offerStore.optionalAmenities"
        v-model:selected-amenities="selectedAmenitiesIds"
    />

    <!-- Things to Know Section -->
    <FAQ />
    
    <CustomModal :show="showFlightOptionsModal" @close="showFlightOptionsModal = false">
      <FlightOptions @close="showFlightOptionsModal = false"/>
    </CustomModal>

    <div id="map-overlay" class="map-popup-overlay hidden" @click="closeMapPopup">
      <div id="map-overlay-map" class="map-popup-map" @click.stop>
        <!-- Close Button -->
        <div
            @click="closeMapPopup"
            class="closemap-button cursor-pointer flex items-center justify-center z-50 absolute border border-black rounded-full p-1.5"
        >
          <img src="@/assets/icons/svg/Close_16x16.svg" alt="">
        </div>
      </div>
    </div>

  </div>


  <TripAddModal
      v-model:show="showAddTripModal"
      @on-trip-created="tripsStore.getAll"
      :date-from="offerStore.selections.dateFrom"
      :date-to="offerStore.selections.dateTo"
      :yacht-location="offerStore.yacht?.location ?? null"
      :yacht="{ yacht: offerStore.yacht, price: offerStore.priceDetails.basePrice, finalPrice: offerStore.priceDetails.total, discount: offerStore.availabilities[1]?.discount, passengers: offerStore.selections.persons }"
      :amenities="offerStore.optionalAmenities"
      :selected-amenities="offerStore.selectedAmenities"
  />
</template>

<style scoped>
:deep(.always-enabled .v-selection-control--disabled) {
  opacity: 1;
}

:deep(.always-enabled.v-input--horizontal) {
  grid-template-areas: unset !important;
}

.add-trip__btn:hover {
  border-color: #000 !important;
}

.flight-options-button:hover img {
  filter: brightness(0) invert(1);
}

.map-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-map {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

.closemap-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  background: white;
  border: 3px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.closemap-button img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hidden {
  display: none;
}

@media (max-width: 1640px) {
  .boat-data-header-top .boat-data-header-left .location div {
    font-size: 0.6875rem !important;
  }

  .boat-data-header-top .boat-data-header-left .location img {
    height: 16px !important;
  }

  .boat-name .boat-name__text {
    font-size: 1.875rem;
  }

  .boat-data-header-left .date-subtitle,
  .boat-data-header-left .passengers-subtitle {
    font-size: .75rem;
  }

  .boat-data-header-left .date-content,
  .boat-data-header-left .passengers-content {
    font-size: 1.5rem;
  }

  .boat-data-header-right .header-title {
    font-size: 1.125rem;
  }

  .boat-data-header-right .boat-charges {
    font-size: 0.75rem;
  }

  .boat-data-header-right .total-text {
    font-size: .875rem;
  }

  .boat-data-header-right .final-price {
    font-size: 1.5rem;
  }
}

@media (max-width: 1538px) {
  .boat-overview-right-title {
    font-size: 1.5rem;
  }

  .boat-overview-right-paragraph {
    font-size: 1rem;
  }

  .boat-overview-right-read-more {
    font-size: 0.875rem;
  }

  .boat-overview-right-title-eq {
    font-size: 1.125rem;
  }

  .boat-overview-right-pill {
    font-size: 0.75rem;
  }

  .boat-overview-right-show-more {
    font-size: 0.875rem;
  }
  
  .faq-title {
    font-size: 1.5rem;
  }

  :deep(.el-collapse-item .el-collapse-item__header) {
    font-size: 1.125rem;
    background: #f8f8f8;
  }
}

@media (max-width: 1450px) {
  .data-iconboxes img {
    height: 32px;
  }

  .data-iconboxes .iconbox__text {
    font-size: 1rem;
  }
}

@media (max-width: 1280px) {
  .boat-name .boat-name__text {
    font-size: 1.625rem;
  }

  .boat-data-header-left .passengers-content,
  .boat-data-header-left .date-content {
    font-size: 1.375rem;
  }
}

@media (max-width: 1160px) {
  .boat-overview-content {
    flex-direction: column;
  }

  .boat-overview-content .boat-overview-content-left {
    width: 100%;
  }

  .boat-overview-content .boat-overview-content-right {
    width: 100%;
  }

  .boat-overview-content-left .main-image {
    height: 400px;
  }

  .boat-overview-content-right {
    padding-right: 0;
    padding-left: 0;
  }

  .main-image {
    border-width: thin !important;
    border-style: solid !important;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  }
}

@media (max-width: 1110px) {
  .boat-data-header-top {
    flex-direction: column;
  }

  .boat-data-header-left, .boat-data-header-right {
    width: 100%;
  }

  .boat-data-header-left .boat-name {
    justify-content: space-between;
  }

  .data-iconboxes img {
    height: 28px;
  }
}

@media (max-width: 1055px ) {
  .boat-data-header-info{
    overflow-x: auto;
    padding-left: 0!important;
    padding-right: 0!important;
  }
}

@media (max-width: 985px) {
  .extras-price {
    font-size: .875rem;
  }

  :deep(.popup-image) {
    height: 45vh !important;
  }
}

@media (max-width: 768px) {
  .img-dp{
    display: none;
  }
  .c-inside{
    padding-bottom: 15px!important;
  }


  .boat-data-header-info{
    overflow-x: auto;
    padding-left: 0!important;
    padding-right: 0!important;
  }

  .boat-data-header-sticky-small {
    z-index: 10;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
  }

  .boat-data-fixed {
    display: flex;
  }

  .boat-data-header-mobi {
    display: flex;
  }

  .boat-overview-content-right {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  .data-iconboxes {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }

  .main-yacht-page-container {
    margin-top: 6.25rem
  }

  .boat-overview-content {
    margin-top: 1rem !important;
    margin-bottom: 0 !important;
  }

  .iconbox-container {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  .boat-overview-right-title, .charges-title {
    font-size: 1.25rem;
  }
  
  :deep(.el-collapse-item .el-collapse-item__header) {
    font-size: 1rem !important;
    background: #f8f8f8;
  }

  :deep(.el-collapse-item__content) {
    font-size: .875rem !important;
  }
}

@media (max-width: 580px) {

  .boat-data-header-bottom{
    padding-left: 10px!important;
    padding-right: 20px!important;
  }
  
  .v-selection-control {
    margin-top: 10px !important;
  }

  .boat-overview-right-title {
    font-size: 1.25rem;
  }

  .boat-overview-right-paragraph,
  .charges-subtitle {
    font-size: .875rem;
  }

  .boat-overview-right-read-more {
    font-size: 0.75rem;
  }
  
  .boat-overview-right-pill {
    font-size: 0.6875rem;
  }

  .boat-overview-right-show-more {
    font-size: 0.75rem;
  }

  .boat-overview-content-left .main-image {
    height: 55vw;
  }

}

@media (max-width: 450px) {
  .boat-data-fixed .req__btn {
    font-size: .75rem;
  }

  .map-popup-map {
    width: 95%;
    height: 95%;
    background-color: white;
    border-radius: 10px;
    position: relative;
  }

  .boat-data-fixed .date-passengers__text {
    font-size: .75rem;
  }

  .boat-data-fixed .price {
    font-size: .875rem;
  }
}


/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

:deep(.v-field__input input) {
  font-size: 24px !important;
}

:deep(.v-select__menu-icon) {
  color: black !important;
}


:deep(.swiper-pagination) {
  text-align:start;
  margin-left: 5px;
  margin-bottom: 10px;
}
:deep(.swiper-pagination-bullet-active) {
  background: white;
}
:deep(.swiper-pagination-bullet){
  background: white;
}


:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  width: 35px ;
  height: 35px;
  background-color: white ;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.swiper-button-prev:after),
:deep(.swiper-button-next:after) {
  font-size: 16px ;
  color: black;
  font-weight: bold;
}

:deep(.swiper-button-prev) {
  left: 10px;
}

:deep(.swiper-button-next) {
  right: 10px;
}


</style>

